import { type ObjectMode } from '@databases/types';
import {
  type Database,
  type DatabaseEntry,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue,
} from '@databases/types/database';
import { createField, getExistingDatabase } from '@databases/utils/database/utils';

import { databasesToContentId } from './constants';

const statusOptions = [
  { color: 'greenLight' as const, id: 'status-1', label: 'Done' },
  { color: 'blueLight' as const, id: 'status-2', label: 'In Progress' },
  { color: 'yellowLight' as const, id: 'status-3', label: 'Review' },
  { color: 'redLight' as const, id: 'status-4', label: 'Blocked' },
  { color: 'purpleLight' as const, id: 'status-5', label: 'Backlog' },
];

const priorityOptions = [
  { color: 'redLight' as const, id: 'prio-1', label: 'Critical' },
  { color: 'orangeLight' as const, id: 'prio-2', label: 'High' },
  { color: 'yellowLight' as const, id: 'prio-3', label: 'Medium' },
  { color: 'blueLight' as const, id: 'prio-4', label: 'Low' },
];

const tagOptions = [
  { color: 'tealLight' as const, id: 'tag-1', label: 'Frontend' },
  { color: 'purpleLight' as const, id: 'tag-2', label: 'Backend' },
  { color: 'greenLight' as const, id: 'tag-3', label: 'Design' },
  { color: 'orangeLight' as const, id: 'tag-4', label: 'Infrastructure' },
  { color: 'blueLight' as const, id: 'tag-5', label: 'Testing' },
];

const standardFields = (): DatabaseField[] => {
  const statusField = createField(DatabaseFieldType.SELECT, {
    id: 'field-status',
    name: 'Status',
  });
  const priorityField = createField(DatabaseFieldType.SELECT, {
    id: 'field-priority',
    name: 'Priority',
  });

  return [
    createField(DatabaseFieldType.TEXT, { id: 'field-task', name: 'Task' }),
    {
      ...statusField,
      settings: { ...statusField.settings, multi: false, selectOptions: statusOptions },
    },
    createField(DatabaseFieldType.USER, { id: 'field-assignee', name: 'Assignee' }),
    createField(DatabaseFieldType.DATE, { id: 'field-due-date', name: 'Due Date' }),
    {
      ...priorityField,
      settings: {
        ...priorityField.settings,
        multi: false,
        selectOptions: priorityOptions,
      },
    },
    createField(DatabaseFieldType.HYPERLINK, { id: 'field-docs', name: 'Documentation' }),
  ];
};

const overflowFields = (): DatabaseField[] => {
  const statusField = createField(DatabaseFieldType.SELECT, {
    id: 'field-status',
    name: 'Status',
  });
  const priorityField = createField(DatabaseFieldType.SELECT, {
    id: 'field-priority',
    name: 'Priority',
  });
  const tagsField = createField(DatabaseFieldType.SELECT, {
    id: 'field-tags',
    name: 'Tags',
  });

  return [
    createField(DatabaseFieldType.TEXT, {
      id: 'field-task',
      name: 'Task',
      width: 260,
    } as Partial<DatabaseField<DatabaseFieldType.TEXT>>),
    {
      ...statusField,
      width: 150,
      settings: { ...statusField.settings, multi: false, selectOptions: statusOptions },
    },
    createField(DatabaseFieldType.USER, {
      id: 'field-assignee',
      name: 'Assignee',
      width: 180,
    } as Partial<DatabaseField<DatabaseFieldType.USER>>),
    createField(DatabaseFieldType.DATE, {
      id: 'field-due-date',
      name: 'Due Date',
      width: 150,
    } as Partial<DatabaseField<DatabaseFieldType.DATE>>),
    {
      ...priorityField,
      width: 140,
      settings: {
        ...priorityField.settings,
        multi: false,
        selectOptions: priorityOptions,
      },
    },
    createField(DatabaseFieldType.NUMBER, {
      id: 'field-budget',
      name: 'Budget ($)',
      width: 130,
    } as Partial<DatabaseField<DatabaseFieldType.NUMBER>>),
    createField(DatabaseFieldType.HYPERLINK, {
      id: 'field-docs',
      name: 'Documentation',
      width: 240,
    } as Partial<DatabaseField<DatabaseFieldType.HYPERLINK>>),
    {
      ...tagsField,
      width: 200,
      settings: { ...tagsField.settings, multi: true, selectOptions: tagOptions },
    },
    createField(DatabaseFieldType.TEXT, {
      id: 'field-notes',
      name: 'Notes',
      width: 250,
    } as Partial<DatabaseField<DatabaseFieldType.TEXT>>),
  ];
};

type EntryRow = Record<string, DatabaseValue<DatabaseFieldType, object>>;

const text = (value: string): DatabaseValue<DatabaseFieldType.TEXT, object> => ({
  type: DatabaseFieldType.TEXT,
  value: { plain: value },
});

const number = (value: string): DatabaseValue<DatabaseFieldType.NUMBER, object> => ({
  type: DatabaseFieldType.NUMBER,
  value,
});

const date = (value: string): DatabaseValue<DatabaseFieldType.DATE, object> => ({
  type: DatabaseFieldType.DATE,
  value,
});

const user = (
  ...users: Array<{ accountId: string; displayName: string }>
): DatabaseValue<DatabaseFieldType.USER, object> => ({
  type: DatabaseFieldType.USER,
  value: users.map((u, i) => ({ _id: `user-${i}`, ...u })),
});

const select = (...ids: string[]): DatabaseValue<DatabaseFieldType.SELECT, object> => ({
  type: DatabaseFieldType.SELECT,
  value: ids,
});

const hyperlink = (
  ...urls: string[]
): DatabaseValue<DatabaseFieldType.HYPERLINK, object> => ({
  type: DatabaseFieldType.HYPERLINK,
  value: urls.map((url, i) => ({ _id: `link-${i}`, url })),
});

const STANDARD_ENTRY_DATA: EntryRow[] = [
  {
    'field-task': text('Implement authentication flow'),
    'field-status': select('status-2'),
    'field-assignee': user({ accountId: 'user-1', displayName: 'Alice Chen' }),
    'field-due-date': date('2026-06-15'),
    'field-priority': select('prio-1'),
    'field-docs': hyperlink('https://docs.example.com/auth-flow'),
  },
  {
    'field-task': text('Design landing page mockups'),
    'field-status': select('status-1'),
    'field-assignee': user({ accountId: 'user-2', displayName: 'Bob Martinez' }),
    'field-due-date': date('2026-05-28'),
    'field-priority': select('prio-2'),
    'field-docs': hyperlink('https://figma.com/file/landing-v2'),
  },
  {
    'field-task': text('Set up CI/CD pipeline'),
    'field-status': select('status-1'),
    'field-assignee': user({ accountId: 'user-3', displayName: 'Carol Nguyen' }),
    'field-due-date': date('2026-05-20'),
    'field-priority': select('prio-2'),
    'field-docs': hyperlink('https://docs.example.com/ci-cd'),
  },
  {
    'field-task': text('Write API documentation'),
    'field-status': select('status-3'),
    'field-assignee': user({ accountId: 'user-4', displayName: 'David Park' }),
    'field-due-date': date('2026-06-10'),
    'field-priority': select('prio-3'),
    'field-docs': hyperlink('https://docs.example.com/api-v2'),
  },
  {
    'field-task': text('Database schema migration'),
    'field-status': select('status-4'),
    'field-assignee': user({ accountId: 'user-1', displayName: 'Alice Chen' }),
    'field-due-date': date('2026-06-01'),
    'field-priority': select('prio-1'),
    'field-docs': hyperlink('https://docs.example.com/migrations'),
  },
  {
    'field-task': text('Performance audit'),
    'field-status': select('status-5'),
    'field-assignee': user({ accountId: 'user-5', displayName: 'Eva Thompson' }),
    'field-due-date': date('2026-07-01'),
    'field-priority': select('prio-4'),
    'field-docs': hyperlink('https://docs.example.com/perf'),
  },
  {
    'field-task': text('User onboarding wizard'),
    'field-status': select('status-2'),
    'field-assignee': user({ accountId: 'user-2', displayName: 'Bob Martinez' }),
    'field-due-date': date('2026-06-20'),
    'field-priority': select('prio-2'),
    'field-docs': hyperlink('https://docs.example.com/onboarding'),
  },
  {
    'field-task': text('Accessibility compliance review'),
    'field-status': select('status-3'),
    'field-assignee': user(
      { accountId: 'user-3', displayName: 'Carol Nguyen' },
      { accountId: 'user-4', displayName: 'David Park' }
    ),
    'field-due-date': date('2026-06-12'),
    'field-priority': select('prio-3'),
    'field-docs': hyperlink('https://docs.example.com/a11y'),
  },
  {
    'field-task': text('Integrate analytics SDK'),
    'field-status': select('status-2'),
    'field-assignee': user({ accountId: 'user-5', displayName: 'Eva Thompson' }),
    'field-due-date': date('2026-06-05'),
    'field-priority': select('prio-3'),
    'field-docs': hyperlink('https://docs.example.com/analytics'),
  },
  {
    'field-task': text('Release v2.0 beta'),
    'field-status': select('status-5'),
    'field-assignee': user({ accountId: 'user-1', displayName: 'Alice Chen' }),
    'field-due-date': date('2026-07-15'),
    'field-priority': select('prio-1'),
    'field-docs': hyperlink('https://docs.example.com/release-v2'),
  },
];

const OVERFLOW_ENTRY_DATA: EntryRow[] = [
  {
    'field-task': text('Implement authentication flow'),
    'field-status': select('status-2'),
    'field-assignee': user({ accountId: 'user-1', displayName: 'Alice Chen' }),
    'field-due-date': date('2026-06-15'),
    'field-priority': select('prio-1'),
    'field-budget': number('45000'),
    'field-docs': hyperlink('https://docs.example.com/auth-flow'),
    'field-tags': select('tag-1', 'tag-2'),
    'field-notes': text('Blocked on SSO provider setup'),
  },
  {
    'field-task': text('Design landing page mockups'),
    'field-status': select('status-1'),
    'field-assignee': user({ accountId: 'user-2', displayName: 'Bob Martinez' }),
    'field-due-date': date('2026-05-28'),
    'field-priority': select('prio-2'),
    'field-budget': number('12000'),
    'field-docs': hyperlink('https://figma.com/file/landing-v2'),
    'field-tags': select('tag-3'),
    'field-notes': text('Final review approved'),
  },
  {
    'field-task': text('Set up CI/CD pipeline'),
    'field-status': select('status-1'),
    'field-assignee': user({ accountId: 'user-3', displayName: 'Carol Nguyen' }),
    'field-due-date': date('2026-05-20'),
    'field-priority': select('prio-2'),
    'field-budget': number('8500'),
    'field-docs': hyperlink('https://docs.example.com/ci-cd'),
    'field-tags': select('tag-4'),
    'field-notes': text('Using GitHub Actions'),
  },
  {
    'field-task': text('Write API documentation'),
    'field-status': select('status-3'),
    'field-assignee': user({ accountId: 'user-4', displayName: 'David Park' }),
    'field-due-date': date('2026-06-10'),
    'field-priority': select('prio-3'),
    'field-budget': number('3000'),
    'field-docs': hyperlink('https://docs.example.com/api-v2'),
    'field-tags': select('tag-2', 'tag-5'),
    'field-notes': text('OpenAPI spec in progress'),
  },
  {
    'field-task': text('Database schema migration'),
    'field-status': select('status-4'),
    'field-assignee': user({ accountId: 'user-1', displayName: 'Alice Chen' }),
    'field-due-date': date('2026-06-01'),
    'field-priority': select('prio-1'),
    'field-budget': number('25000'),
    'field-docs': hyperlink('https://docs.example.com/migrations'),
    'field-tags': select('tag-2', 'tag-4'),
    'field-notes': text('Waiting on DBA approval'),
  },
  {
    'field-task': text('Performance audit'),
    'field-status': select('status-5'),
    'field-assignee': user({ accountId: 'user-5', displayName: 'Eva Thompson' }),
    'field-due-date': date('2026-07-01'),
    'field-priority': select('prio-4'),
    'field-budget': number('7500'),
    'field-docs': hyperlink('https://docs.example.com/perf'),
    'field-tags': select('tag-1', 'tag-5'),
    'field-notes': text('Lighthouse score target: 95+'),
  },
  {
    'field-task': text('User onboarding wizard'),
    'field-status': select('status-2'),
    'field-assignee': user({ accountId: 'user-2', displayName: 'Bob Martinez' }),
    'field-due-date': date('2026-06-20'),
    'field-priority': select('prio-2'),
    'field-budget': number('18000'),
    'field-docs': hyperlink('https://docs.example.com/onboarding'),
    'field-tags': select('tag-1', 'tag-3'),
    'field-notes': text('A/B test planned for week 24'),
  },
  {
    'field-task': text('Accessibility compliance review'),
    'field-status': select('status-3'),
    'field-assignee': user(
      { accountId: 'user-3', displayName: 'Carol Nguyen' },
      { accountId: 'user-4', displayName: 'David Park' }
    ),
    'field-due-date': date('2026-06-12'),
    'field-priority': select('prio-3'),
    'field-budget': number('5000'),
    'field-docs': hyperlink('https://docs.example.com/a11y'),
    'field-tags': select('tag-1', 'tag-5'),
    'field-notes': text('WCAG 2.1 AA compliance'),
  },
  {
    'field-task': text('Integrate analytics SDK'),
    'field-status': select('status-2'),
    'field-assignee': user({ accountId: 'user-5', displayName: 'Eva Thompson' }),
    'field-due-date': date('2026-06-05'),
    'field-priority': select('prio-3'),
    'field-budget': number('15000'),
    'field-docs': hyperlink('https://docs.example.com/analytics'),
    'field-tags': select('tag-1', 'tag-2'),
    'field-notes': text('Segment + Amplitude integration'),
  },
  {
    'field-task': text('Release v2.0 beta'),
    'field-status': select('status-5'),
    'field-assignee': user({ accountId: 'user-1', displayName: 'Alice Chen' }),
    'field-due-date': date('2026-07-15'),
    'field-priority': select('prio-1'),
    'field-budget': number('60000'),
    'field-docs': hyperlink('https://docs.example.com/release-v2'),
    'field-tags': select('tag-1', 'tag-2', 'tag-4'),
    'field-notes': text('Major milestone - all teams involved'),
  },
];

const buildEntriesById = (
  baseDatabase: Database<ObjectMode.EXISTING>,
  entryData: EntryRow[]
): Record<string, DatabaseEntry> => {
  const result: Record<string, DatabaseEntry> = {};
  baseDatabase.entries.forEach((entryId, index) => {
    result[entryId] = {
      id: entryId,
      values: entryData[index] ?? {},
    };
  });
  return result;
};

export const getDatabaseWithRichContent = (): Database<ObjectMode.EXISTING> => {
  const fields = standardFields();
  const baseDatabase = getExistingDatabase({
    contentId: databasesToContentId['databaseWithRichContent'],
    databaseName: 'Project Tracker',
    entryCount: STANDARD_ENTRY_DATA.length,
  });

  const entriesById = buildEntriesById(baseDatabase, STANDARD_ENTRY_DATA);

  return {
    ...baseDatabase,
    fields: fields.map((field) => field.id),
    fieldsById: Object.fromEntries(fields.map((field) => [field.id, field])),
    entriesById,
    entries: Object.keys(entriesById),
  };
};

export const getDatabaseWithRichContentOverflow = (): Database<ObjectMode.EXISTING> => {
  const fields = overflowFields();
  const baseDatabase = getExistingDatabase({
    contentId: databasesToContentId['databaseWithRichContentOverflow'],
    databaseName: 'Project Tracker (Full)',
    entryCount: OVERFLOW_ENTRY_DATA.length,
  });

  const entriesById = buildEntriesById(baseDatabase, OVERFLOW_ENTRY_DATA);

  return {
    ...baseDatabase,
    fields: fields.map((field) => field.id),
    fieldsById: Object.fromEntries(fields.map((field) => [field.id, field])),
    entriesById,
    entries: Object.keys(entriesById),
  };
};
